import { ForgetTvControl, LinkedAccount } from './StatusHints';
import type { TvNotice, TvPuttEvent, TvSnapshot } from './protocol';
import MetricsHud from './MetricsHud';
import { GatePill, NoticeStrip } from './StatusHints';
import { displayTitle } from './presentation';

export interface SessionMetrics {
  event: TvPuttEvent | null;
  sessionN: number;
  sessionAvgFt: number | null;
}

interface AmbientOverlayProps {
  snapshot: TvSnapshot | null;
  /** Non-null renders the metrics surface (big readouts) in place of the
   * ambient copy: Start Line, or Target/Distance in their METRICS view. */
  metrics: SessionMetrics | null;
  /** The visible interrupt (App applies `noticeVisible`); sits under the
   * card text, or under the readouts on the metrics surface. */
  notice?: TvNotice | null;
  /** The phone is on a menu: show the calm start screen, no menu. */
  startScreen?: boolean;
  /** The account this TV is paired to, shown on the start screen. */
  ownerHandle?: string | null;
  /** "Pair with a different account", offered on the start screen. */
  onForget?: (() => void) | null;
}

/**
 * Connected, nothing running (TV_REVIVAL_PLAN.md §2.2 'ambient'): the course
 * scenery stays behind this layer, with "Open a mode on your phone" and the
 * gate status. The same layer carries the metrics surface for modes that
 * grade putts without a 3D scene.
 */
export default function AmbientOverlay({ snapshot, metrics, notice = null, startScreen = false, ownerHandle = null, onForget = null }: AmbientOverlayProps) {
  const title = startScreen ? 'Start a session on your device' : displayTitle(snapshot?.title);
  const subtitle = startScreen ? null : snapshot?.subtitle?.trim() || 'Start a session on your device.';
  return (
    <div className={`ambient-overlay${metrics ? ' ambient-overlay-metrics' : ''}`}>
      <GateStatus snapshot={snapshot} />
      {metrics ? (
        <section className="metrics-stage" aria-live="polite">
          <MetricsHud
            title={snapshot?.mode === 'startline' ? 'Start Line' : title}
            event={metrics.event}
            sessionN={metrics.sessionN}
            sessionAvgFt={metrics.sessionAvgFt}
          />
          <NoticeStrip notice={notice} placement="card" />
        </section>
      ) : (
        // Start screen (Cole, 2026-09-26): logo, wordmark and tagline big
        // and centered, then the one instruction. Never a corner card.
        <section className="ambient-hero" aria-live="polite">
          <div className="ambient-logo">
            <img src="/lagking-flag.png" alt="" />
          </div>
          <div className="brand ambient-hero-brand">LAG<span>KING</span></div>
          <p className="ambient-hero-tagline">Putting practice made fun.</p>
          <h2 className="ambient-hero-cta">
            {snapshot ? 'Start a session on your device' : 'Open LagKing on your device'}
          </h2>
          <LinkedAccount handle={ownerHandle} />
          <NoticeStrip notice={notice} placement="card" />
          {onForget ? <ForgetTvControl onForget={onForget} /> : null}
        </section>
      )}
    </div>
  );
}

/** Gate pill for the ambient, mirror, and card screens; the course HUD
 * places the same pill in its own corner. */
export function GateStatus({ snapshot }: { snapshot: TvSnapshot | null }) {
  return <GatePill snapshot={snapshot} className="companion-gate ambient-gate" />;
}
